import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Heart, MessageCircle, Eye, Share2, MousePointerClick, TrendingUp,
  BarChart3, AtSign, RefreshCw, Loader2,
} from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

const PLATFORM_ICONS: Record<string, React.ElementType> = {
  instagram: Instagram, twitter: Twitter, linkedin: Linkedin,
  facebook: Facebook, threads: AtSign,
};
const PLATFORM_COLORS: Record<string, string> = {
  instagram: "#e1306c", twitter: "#1da1f2", linkedin: "#0077b5",
  facebook: "#1877f2", threads: "#101010",
};

function fmtK(n: number | null | undefined) {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function StatBox({ label, value, icon: Icon, color }: { label: string; value: string; icon: React.ElementType; color: string }) {
  return (
    <div className="bg-muted/40 rounded-xl p-4 flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className={`w-3.5 h-3.5 ${color}`} />{label}
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

export function PostPerformanceView() {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const [range, setRange] = useState("30");
  const [selectedPost, setSelectedPost] = useState<any | null>(null);
  const [syncing, setSyncing] = useState(false);

  const { data: posts = [], refetch } = useQuery({
    queryKey: ["post-performance", orgId, range],
    queryFn: async () => {
      const since = new Date(Date.now() - Number(range) * 86400000).toISOString();
      let q = (supabase as any)
        .from("posts")
        .select("id,content,platform,status,published_at,created_at,platform_post_id,social_account_id,social_accounts(account_handle,avatar_url),post_analytics(likes_count,comments_count,shares_count,reach_count,impressions_count,clicks_count,engagement_rate,synced_at)")
        .eq("status", "published")
        .gte("created_at", since)
        .order("published_at", { ascending: false });
      if (orgId) q = q.eq("org_id", orgId);
      const { data } = await q;
      return (data ?? []) as any[];
    },
  });

  const handleSyncAnalytics = async () => {
    setSyncing(true);
    try {
      await supabase.functions.invoke("sync-analytics", { body: {} });
      await refetch();
      toast({ title: "Analytics synced" });
    } catch {
      toast({ title: "Sync failed", variant: "destructive" });
    } finally {
      setSyncing(false);
    }
  };

  const withAnalytics = posts.filter(p => p.post_analytics);
  const withoutAnalytics = posts.filter(p => !p.post_analytics);

  const topByEngagement = [...withAnalytics].sort((a, b) =>
    (b.post_analytics?.engagement_rate ?? 0) - (a.post_analytics?.engagement_rate ?? 0)
  );

  const totalLikes       = withAnalytics.reduce((s, p) => s + (p.post_analytics?.likes_count ?? 0), 0);
  const totalComments    = withAnalytics.reduce((s, p) => s + (p.post_analytics?.comments_count ?? 0), 0);
  const totalImpressions = withAnalytics.reduce((s, p) => s + (p.post_analytics?.impressions_count ?? 0), 0);
  const totalReach       = withAnalytics.reduce((s, p) => s + (p.post_analytics?.reach_count ?? 0), 0);
  const avgEngagement    = withAnalytics.length > 0
    ? (withAnalytics.reduce((s, p) => s + (p.post_analytics?.engagement_rate ?? 0), 0) / withAnalytics.length).toFixed(2)
    : "0";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" /> Post Performance
          </h2>
          <p className="text-sm text-muted-foreground">Per-post analytics across all platforms</p>
        </div>
        <div className="flex gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={handleSyncAnalytics} disabled={syncing}>
            {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            {syncing ? "Syncing…" : "Sync Analytics"}
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      {withAnalytics.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatBox label="Posts tracked" value={String(withAnalytics.length)} icon={BarChart3} color="text-blue-500" />
          <StatBox label="Total Likes" value={fmtK(totalLikes)} icon={Heart} color="text-rose-500" />
          <StatBox label="Comments" value={fmtK(totalComments)} icon={MessageCircle} color="text-amber-500" />
          <StatBox label="Reach" value={fmtK(totalReach)} icon={Eye} color="text-emerald-500" />
          <StatBox label="Avg Engagement" value={`${avgEngagement}%`} icon={TrendingUp} color="text-purple-500" />
        </div>
      )}

      {posts.length === 0 ? (
        <Card className="p-10 text-center">
          <BarChart3 className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">No published posts in this period</p>
          <p className="text-sm text-muted-foreground mt-1">Publish posts and sync analytics to see performance data.</p>
        </Card>
      ) : (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm">{posts.length} posts · {withAnalytics.length} with analytics</h3>
            {withoutAnalytics.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">{withoutAnalytics.length} pending sync</Badge>
            )}
          </div>
          <div className="space-y-2">
            {topByEngagement.concat(withoutAnalytics).map(post => {
              const Icon = PLATFORM_ICONS[post.platform] ?? AtSign;
              const color = PLATFORM_COLORS[post.platform] ?? "#888";
              const a = post.post_analytics;
              return (
                <button
                  key={post.id}
                  onClick={() => setSelectedPost(post)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-muted/30 transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: color }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{post.content?.slice(0, 80)}{(post.content?.length ?? 0) > 80 ? "…" : ""}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {post.published_at ? format(new Date(post.published_at), "MMM d, yyyy") : "Unpublished"}
                      {post.social_accounts?.account_handle && ` · @${post.social_accounts.account_handle}`}
                    </p>
                  </div>
                  {a ? (
                    <div className="flex items-center gap-4 shrink-0 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{fmtK(a.likes_count)}</span>
                      <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{fmtK(a.comments_count)}</span>
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{fmtK(a.reach_count)}</span>
                      <Badge variant="secondary" className="text-[10px]">
                        {Number(a.engagement_rate ?? 0).toFixed(2)}% eng.
                      </Badge>
                    </div>
                  ) : (
                    <Badge variant="outline" className="text-[10px] shrink-0">No data</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
        <DialogContent className="max-w-lg">
          {selectedPost && (() => {
            const Icon = PLATFORM_ICONS[selectedPost.platform] ?? AtSign;
            const color = PLATFORM_COLORS[selectedPost.platform] ?? "#888";
            const a = selectedPost.post_analytics;
            const engRate = a?.engagement_rate ? Number(a.engagement_rate).toFixed(2) : null;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white" style={{ background: color }}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    Post Analytics
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm border rounded-lg p-3 bg-muted/30 leading-relaxed">{selectedPost.content}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="capitalize">{selectedPost.platform}</span>
                    {selectedPost.social_accounts?.account_handle && <span>@{selectedPost.social_accounts.account_handle}</span>}
                    {selectedPost.published_at && <span>· {format(new Date(selectedPost.published_at), "MMM d, yyyy 'at' HH:mm")}</span>}
                  </div>

                  {a ? (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-muted/40 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold">{fmtK(a.likes_count)}</p>
                          <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5"><Heart className="w-3 h-3 text-rose-500" />Likes</p>
                        </div>
                        <div className="bg-muted/40 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold">{fmtK(a.comments_count)}</p>
                          <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5"><MessageCircle className="w-3 h-3 text-amber-500" />Comments</p>
                        </div>
                        <div className="bg-muted/40 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold">{fmtK(a.shares_count)}</p>
                          <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5"><Share2 className="w-3 h-3 text-blue-500" />Shares</p>
                        </div>
                        <div className="bg-muted/40 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold">{fmtK(a.reach_count)}</p>
                          <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5"><Eye className="w-3 h-3 text-emerald-500" />Reach</p>
                        </div>
                        <div className="bg-muted/40 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold">{fmtK(a.impressions_count)}</p>
                          <p className="text-[10px] text-muted-foreground">Impressions</p>
                        </div>
                        <div className="bg-muted/40 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold">{fmtK(a.clicks_count)}</p>
                          <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5"><MousePointerClick className="w-3 h-3 text-purple-500" />Clicks</p>
                        </div>
                      </div>
                      {engRate && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                          <TrendingUp className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium">Engagement Rate: <strong>{engRate}%</strong></span>
                        </div>
                      )}
                      {a.synced_at && (
                        <p className="text-[10px] text-muted-foreground text-right">
                          Analytics last synced {formatDistanceToNow(new Date(a.synced_at), { addSuffix: true })}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <BarChart3 className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
                      <p className="text-sm text-muted-foreground">No analytics data yet. Click "Sync Analytics" to fetch performance data.</p>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
